/**
 * Whitelabel API client for New_DSA_Portal
 * Communicates with LOS backend for white labelling configuration and asset upload
 */

import { BaseApi } from "./base.api";
import { LOS_API_BASE_URL } from "@/config";

export interface WhitelabelConfig {
  agent_id: string;
  company_name: string;
  name?: string;
  logo_url: string;
  logo?: string;
  use_custom_theme?: boolean;
  theme_color?: string;
  accent_color?: string;
  is_active: boolean;
  vars?: Record<string, string>;
  blue?: Record<number, string>;
  cta?: {
    bg: string;
    fg: string;
    hover: string;
    active: string;
  };
  created_at?: string;
  updated_at?: string;
}

export interface SaveWhitelabelRequest {
  agent_id: string;
  company_name: string;
  logo_url: string;
  use_custom_theme?: boolean;
  theme_color?: string;
  accent_color?: string;
  is_active: boolean;
}

export interface UploadLogoRequest {
  agent_id: string;
  filename: string;
  image_data: string; // base64 string
  contentType?: string;
}

export interface UploadLogoResponse {
  logo_url: string;
  s3_key: string;
  filename: string;
  contentType: string;
}

interface ApiResponseWrapper<T> {
  status: string;
  message: string;
  response: T;
}

export class WhitelabelApi extends BaseApi {
  constructor() {
    super(LOS_API_BASE_URL);
  }

  /**
   * Fetch current white labelling configuration for a DSA agent.
   */
  async getWhitelabelConfig(agentId: string): Promise<WhitelabelConfig | null> {
    try {
      const response = await this.post<ApiResponseWrapper<WhitelabelConfig>>(
        "/dsa/whitelabel/get",
        { agent_id: agentId },
        true
      );

      if (response.success && response.data?.response) {
        return response.data.response;
      }
      return null;
    } catch (error) {
      console.warn("Failed to fetch DSA whitelabel config:", error);
      return null;
    }
  }

  /**
   * Save or update white labelling configuration for a DSA agent.
   */
  async saveWhitelabelConfig(
    request: SaveWhitelabelRequest
  ): Promise<WhitelabelConfig> {
    try {
      const response = await this.post<ApiResponseWrapper<WhitelabelConfig>>(
        "/dsa/whitelabel/save",
        request,
        true
      );

      if (response.success && response.data?.response) {
        return response.data.response;
      }
      throw new Error(response.message || "Failed to save whitelabel configuration");
    } catch (error) {
      const apiError = this.handleError(error);
      throw new Error(apiError.message || "Failed to save whitelabel configuration");
    }
  }

  /**
   * Upload logo asset to S3 bucket via LOS backend.
   */
  async uploadWhitelabelLogo(
    request: UploadLogoRequest
  ): Promise<UploadLogoResponse> {
    try {
      const response = await this.post<ApiResponseWrapper<UploadLogoResponse>>(
        "/dsa/whitelabel/upload-logo",
        request,
        true
      );

      if (response.success && response.data?.response) {
        return response.data.response;
      }
      throw new Error(response.message || "Failed to upload logo");
    } catch (error) {
      const apiError = this.handleError(error);
      throw new Error(apiError.message || "Failed to upload logo");
    }
  }
}

export const whitelabelApi = new WhitelabelApi();
